import React from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './components/LandingPage';
import AdminDashboard from './components/admin/AdminDashboard';
import { getAdjudicationEventIdFromPath } from './utils/adminRoutes';
import AdminMFASetupPage from './components/admin/AdminMFASetupPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { RegistrationWizard } from './components/registration';
import MissionPage from './components/MissionPage';
import ClubRulesPage from './components/ClubRulesPage';
import CodeOfConductPage from './components/CodeOfConductPage';
import WaiverPage from './components/WaiverPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import LearnMorePage from './components/LearnMorePage';
import FishingRegulationsPage from './components/FishingRegulationsPage';
import AnnouncementsPage from './components/AnnouncementsPage';
import AnnouncementDetailPage from './components/AnnouncementDetailPage';
import GroperArticlePage from './components/GroperArticlePage';
import { GROPER_ARTICLE_PATH } from './content/groperStory';
import ColouringCompPage from './components/ColouringCompPage';
import { COLOURING_PATH } from './content/colouringComp';
import EventsPage from './components/EventsPage';
import MemberDashboard from './components/member/MemberDashboard';
import MemberProfile from './components/member/MemberProfile';
import CompetitionsList from './components/member/CompetitionsList';
import SmartCheckInPage from './components/registration/SmartCheckInPage';
import CatchLandingPage from './components/registration/CatchLandingPage';
import ResumePaymentPage from './components/registration/ResumePaymentPage';
import GuestCatchSubmission from './components/registration/GuestCatchSubmission';
import CompetitionDayDashboard from './components/admin/CompetitionDayDashboard';
import RegistrationDetailView from './components/admin/RegistrationDetailView';
import MemberCatchSubmission from './components/member/MemberCatchSubmission';
import PublicLeaderboard from './components/registration/PublicLeaderboard';
import MyFishingPage from './components/member/MyFishingPage';
import SeasonChampionshipPage from './components/member/SeasonChampionshipPage';
import HappySnapSubmission from './components/member/HappySnapSubmission';
import HappySnapPhotoWall from './components/member/HappySnapPhotoWall';

type PageType =
  | 'home'
  | 'admin'
  | 'admin-setup-mfa'
  | 'register'
  | 'mission'
  | 'rules'
  | 'conduct'
  | 'waiver'
  | 'privacy'
  | 'regulations'
  | 'learn-more'
  | 'announcements'
  | 'announcement-detail'
  | 'groper-article'
  | 'colouring'
  | 'events'
  | 'dashboard'
  | 'profile'
  | 'competitions'
  | 'catches'
  | 'my-stats'
  | 'my-fishing'
  | 'season-championship'
  | 'smart-checkin'
  | 'catch-landing'
  | 'resume-payment'
  | 'admin-competition-day'
  | 'admin-registration-detail'
  | 'member-catch-submit'
  | 'guest-catch-submit'
  | 'public-leaderboard'
  | 'happy-snaps'
  | 'photo-wall';

function App() {
  const [currentPage, setCurrentPage] = React.useState<PageType>('home');
  const [eventId, setEventId] = React.useState<string>('');
  const [adjudicationEventId, setAdjudicationEventId] = React.useState<string>('');
  const [registrationId, setRegistrationId] = React.useState<string>('');
  const [announcementId, setAnnouncementId] = React.useState<string>('');
  const [resumePaymentMemberId, setResumePaymentMemberId] = React.useState<string>('');
  const [resumePaymentToken, setResumePaymentToken] = React.useState<string>('');

  const resolveRoute = React.useCallback(() => {
    const path = window.location.pathname.replace(/\/$/, '');
    if (path.startsWith('/admin/competition-day/registration/')) {
      setRegistrationId(path.split('/').pop() || '');
      setCurrentPage('admin-registration-detail');
    } else if (path.startsWith('/admin/competition-day/')) {
      setEventId(path.split('/').pop() || '');
      setCurrentPage('admin-competition-day');
    } else if (getAdjudicationEventIdFromPath(path)) {
      setAdjudicationEventId(getAdjudicationEventIdFromPath(path));
      setCurrentPage('admin');
    } else if (path === '/admin/setup-mfa') {
      setCurrentPage('admin-setup-mfa');
    } else if (path.startsWith('/admin')) {
      setAdjudicationEventId('');
      setCurrentPage('admin');
    } else if (path === '/check-in') {
      setCurrentPage('smart-checkin');
    } else if (path === '/catch') {
      setCurrentPage('catch-landing');
    } else if (/^\/resume-payment\/[^/]+(?:\/[^/]+)?$/.test(path)) {
      const segments = path.split('/');
      const memberId = segments[2];
      let token = segments[3] || (window.location.hash ? window.location.hash.slice(1) : '');
      try {
        const stored = sessionStorage.getItem('tfc.resumePayment');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.memberId === memberId && typeof parsed?.token === 'string')
            token = parsed.token;
          sessionStorage.removeItem('tfc.resumePayment');
        }
      } catch {
        /* storage may be unavailable; URL parsing above still works */
      }
      setResumePaymentMemberId(memberId);
      setResumePaymentToken(token);
      setCurrentPage('resume-payment');
      if (segments[3] || window.location.hash) {
        window.history.replaceState({}, '', `/resume-payment/${memberId}`);
      }
    } else if (path.startsWith('/competition-check-in/')) {
      // Per-event check-in URL retired 15 Aug 2026 — CheckInPage.tsx was
      // deleted and its content merged into SmartCheckInPage.tsx (one
      // page, one URL, one visual pattern for both member and guest
      // check-in). Redirect any old link/QR/bookmark to /check-in, which
      // auto-resolves the currently open competition itself.
      window.history.replaceState({}, '', '/check-in');
      setCurrentPage('smart-checkin');
    } else if (path.startsWith('/submit-catch')) {
      setCurrentPage('guest-catch-submit');
    } else if (path.startsWith('/register')) {
      setCurrentPage('register');
    } else if (path.startsWith('/mission')) {
      setCurrentPage('mission');
    } else if (path.startsWith('/rules')) {
      setCurrentPage('rules');
    } else if (path.startsWith('/conduct')) {
      setCurrentPage('conduct');
    } else if (path.startsWith('/waiver')) {
      setCurrentPage('waiver');
    } else if (path.startsWith('/privacy')) {
      setCurrentPage('privacy');
    } else if (path.startsWith('/regulations')) {
      setCurrentPage('regulations');
    } else if (path.startsWith('/learn-more')) {
      setCurrentPage('learn-more');
    } else if (path === GROPER_ARTICLE_PATH) {
      setCurrentPage('groper-article');
    } else if (path === COLOURING_PATH) {
      setCurrentPage('colouring');
    } else if (/^\/announcements\/[^/]+$/.test(path)) {
      setAnnouncementId(path.split('/')[2]);
      setCurrentPage('announcement-detail');
    } else if (path.startsWith('/announcements')) {
      setCurrentPage('announcements');
    } else if (path.startsWith('/events')) {
      setCurrentPage('events');
    } else if (path.startsWith('/dashboard')) {
      setCurrentPage('dashboard');
    } else if (path.startsWith('/profile')) {
      setCurrentPage('profile');
    } else if (/^\/competitions\/[^/]+\/results$/.test(path)) {
      setEventId(path.split('/')[2]);
      setCurrentPage('public-leaderboard');
    } else if (/^\/competitions\/[^/]+\/happy-snaps$/.test(path)) {
      setEventId(path.split('/')[2]);
      setCurrentPage('happy-snaps');
    } else if (/^\/competitions\/[^/]+\/photo-wall$/.test(path)) {
      setEventId(path.split('/')[2]);
      setCurrentPage('photo-wall');
    } else if (/^\/competitions\/[^/]+\/submit$/.test(path)) {
      setEventId(path.split('/')[2]);
      setCurrentPage('member-catch-submit');
    } else if (path.startsWith('/catches')) {
      setCurrentPage('my-fishing');
    } else if (path.startsWith('/my-stats')) {
      setCurrentPage('my-fishing');
    } else if (path.startsWith('/my-fishing')) {
      setCurrentPage('my-fishing');
    } else if (path.startsWith('/season-championship')) {
      setCurrentPage('season-championship');
    } else if (path.startsWith('/competitions')) {
      setCurrentPage('competitions');
    } else {
      setCurrentPage('home');
    }
  }, []);

  React.useEffect(() => {
    resolveRoute();
    window.addEventListener('popstate', resolveRoute);
    return () => window.removeEventListener('popstate', resolveRoute);
  }, [resolveRoute]);

  const navigateToAdmin = () => {
    window.history.pushState({}, '', '/admin');
    setCurrentPage('admin');
    window.scrollTo(0, 0);
  };

  const navigateToRegister = () => {
    window.history.pushState({}, '', '/register');
    setCurrentPage('register');
    window.scrollTo(0, 0);
  };

  const navigateToHome = () => {
    window.history.pushState({}, '', '/');
    setCurrentPage('home');
    window.scrollTo(0, 0);
  };

  const navigateToAnnouncements = () => {
    window.history.pushState({}, '', '/announcements');
    setCurrentPage('announcements');
    window.scrollTo(0, 0);
  };

  const navigateToAnnouncementDetail = (id: string) => {
    window.history.pushState({}, '', `/announcements/${id}`);
    setAnnouncementId(id);
    setCurrentPage('announcement-detail');
    window.scrollTo(0, 0);
  };

  const navigateToGroperArticle = () => {
    window.history.pushState({}, '', GROPER_ARTICLE_PATH);
    setCurrentPage('groper-article');
    window.scrollTo(0, 0);
  };

  const navigateToColouring = () => {
    window.history.pushState({}, '', COLOURING_PATH);
    setCurrentPage('colouring');
    window.scrollTo(0, 0);
  };

  const navigateToLearnMore = () => {
    window.history.pushState({}, '', '/learn-more');
    setCurrentPage('learn-more');
    window.scrollTo(0, 0);
  };

  const navigateToMission = () => {
    window.history.pushState({}, '', '/mission');
    setCurrentPage('mission');
    window.scrollTo(0, 0);
  };

  const navigateToRules = () => {
    // Open in new tab to preserve registration form data
    window.open('/rules', '_blank', 'noopener,noreferrer');
  };

  const navigateToConduct = () => {
    // Open in new tab to preserve registration form data
    window.open('/conduct', '_blank', 'noopener,noreferrer');
  };

  const navigateToWaiver = () => {
    // Open in new tab to preserve registration form data
    window.open('/waiver', '_blank', 'noopener,noreferrer');
  };

  const navigateToDashboard = () => {
    window.history.pushState({}, '', '/dashboard');
    setCurrentPage('dashboard');
    window.scrollTo(0, 0);
  };

  const navigateToProfile = () => {
    window.history.pushState({}, '', '/profile');
    setCurrentPage('profile');
    window.scrollTo(0, 0);
  };

  const navigateToCompetitions = () => {
    window.history.pushState({}, '', '/competitions');
    setCurrentPage('competitions');
    window.scrollTo(0, 0);
  };

  const navigateToMyFishing = () => {
    window.history.pushState({}, '', '/my-fishing');
    setCurrentPage('my-fishing');
    window.scrollTo(0, 0);
  };

  const handleMemberNavigate = (
    page:
      | 'dashboard'
      | 'profile'
      | 'competitions'
      | 'catches'
      | 'my-stats'
      | 'my-fishing'
      | 'season-championship'
      | 'home'
  ) => {
    if (page === 'home') {
      navigateToHome();
    } else if (page === 'dashboard') {
      navigateToDashboard();
    } else if (page === 'profile') {
      navigateToProfile();
    } else if (page === 'competitions') {
      navigateToCompetitions();
    } else if (page === 'season-championship') {
      window.history.pushState({}, '', '/season-championship');
      setCurrentPage('season-championship');
    } else if (page === 'catches' || page === 'my-stats' || page === 'my-fishing') {
      navigateToMyFishing();
    }
  };

  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <div className="App">
            {currentPage === 'admin' ? (
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard
                  key={adjudicationEventId || 'admin'}
                  initialAdjudicationEventId={adjudicationEventId || undefined}
                />
              </ProtectedRoute>
            ) : currentPage === 'admin-setup-mfa' ? (
              <ProtectedRoute requireAdmin={true}>
                <AdminMFASetupPage />
              </ProtectedRoute>
            ) : currentPage === 'admin-competition-day' ? (
              <ProtectedRoute requireAdmin={true}>
                <CompetitionDayDashboard eventId={eventId} />
              </ProtectedRoute>
            ) : currentPage === 'admin-registration-detail' ? (
              <ProtectedRoute requireAdmin={true}>
                <RegistrationDetailView registrationId={registrationId} />
              </ProtectedRoute>
            ) : currentPage === 'smart-checkin' ? (
              <SmartCheckInPage />
            ) : currentPage === 'catch-landing' ? (
              <CatchLandingPage />
            ) : currentPage === 'resume-payment' ? (
              <ResumePaymentPage memberId={resumePaymentMemberId} token={resumePaymentToken} />
            ) : currentPage === 'member-catch-submit' ? (
              <ProtectedRoute>
                <MemberCatchSubmission eventId={eventId} />
              </ProtectedRoute>
            ) : currentPage === 'guest-catch-submit' ? (
              <GuestCatchSubmission />
            ) : currentPage === 'public-leaderboard' ? (
              <PublicLeaderboard eventId={eventId} />
            ) : currentPage === 'happy-snaps' ? (
              <ProtectedRoute>
                <HappySnapSubmission
                  eventId={eventId}
                  onBack={() => {
                    window.history.pushState({}, '', '/competitions');
                    setCurrentPage('competitions');
                  }}
                />
              </ProtectedRoute>
            ) : currentPage === 'photo-wall' ? (
              <ProtectedRoute>
                <HappySnapPhotoWall
                  eventId={eventId}
                  onBack={() => {
                    window.history.pushState({}, '', '/competitions');
                    setCurrentPage('competitions');
                  }}
                />
              </ProtectedRoute>
            ) : currentPage === 'dashboard' ? (
              <ProtectedRoute>
                <MemberDashboard onNavigate={handleMemberNavigate} />
              </ProtectedRoute>
            ) : currentPage === 'profile' ? (
              <ProtectedRoute>
                <MemberProfile onNavigate={handleMemberNavigate} />
              </ProtectedRoute>
            ) : currentPage === 'competitions' ? (
              <ProtectedRoute>
                <CompetitionsList onNavigate={handleMemberNavigate} />
              </ProtectedRoute>
            ) : currentPage === 'my-fishing' ? (
              <ProtectedRoute>
                <MyFishingPage onNavigate={handleMemberNavigate} />
              </ProtectedRoute>
            ) : currentPage === 'season-championship' ? (
              <ProtectedRoute>
                <SeasonChampionshipPage onNavigate={handleMemberNavigate} />
              </ProtectedRoute>
            ) : currentPage === 'register' ? (
              <RegistrationWizard
                onComplete={navigateToHome}
                onCancel={navigateToHome}
                onNavigateToRules={navigateToRules}
                onNavigateToConduct={navigateToConduct}
                onNavigateToWaiver={navigateToWaiver}
              />
            ) : currentPage === 'mission' ? (
              <MissionPage
                onNavigateHome={navigateToHome}
                onNavigateToRules={navigateToRules}
                onNavigateToConduct={navigateToConduct}
                onNavigateToWaiver={navigateToWaiver}
              />
            ) : currentPage === 'rules' ? (
              <ClubRulesPage
                onNavigateHome={navigateToHome}
                onNavigateToMission={navigateToMission}
                onNavigateToConduct={navigateToConduct}
                onNavigateToWaiver={navigateToWaiver}
              />
            ) : currentPage === 'conduct' ? (
              <CodeOfConductPage
                onNavigateHome={navigateToHome}
                onNavigateToMission={navigateToMission}
                onNavigateToRules={navigateToRules}
                onNavigateToWaiver={navigateToWaiver}
              />
            ) : currentPage === 'waiver' ? (
              <WaiverPage
                onNavigateHome={navigateToHome}
                onNavigateToMission={navigateToMission}
                onNavigateToRules={navigateToRules}
                onNavigateToConduct={navigateToConduct}
              />
            ) : currentPage === 'regulations' ? (
              <FishingRegulationsPage
                onNavigateHome={navigateToHome}
                onMemberNavigate={handleMemberNavigate}
              />
            ) : currentPage === 'privacy' ? (
              <PrivacyPolicyPage onNavigateHome={navigateToHome} />
            ) : currentPage === 'learn-more' ? (
              <LearnMorePage
                onNavigateHome={navigateToHome}
                onNavigateToRegister={navigateToRegister}
              />
            ) : currentPage === 'announcements' ? (
              <AnnouncementsPage
                onNavigateHome={navigateToHome}
                onNavigateToDetail={navigateToAnnouncementDetail}
              />
            ) : currentPage === 'announcement-detail' ? (
              <AnnouncementDetailPage
                announcementId={announcementId}
                onNavigateHome={navigateToHome}
                onNavigateToAnnouncements={navigateToAnnouncements}
              />
            ) : currentPage === 'groper-article' ? (
              <GroperArticlePage
                onNavigateHome={navigateToHome}
                onNavigateToAnnouncements={navigateToAnnouncements}
              />
            ) : currentPage === 'colouring' ? (
              <ColouringCompPage onNavigateHome={navigateToHome} />
            ) : currentPage === 'events' ? (
              <EventsPage onNavigateHome={navigateToHome} />
            ) : (
              <LandingPage
                onNavigateToAdmin={navigateToAdmin}
                onNavigateToRegister={navigateToRegister}
                onNavigateToMission={navigateToMission}
                onNavigateToRules={navigateToRules}
                onNavigateToConduct={navigateToConduct}
                onNavigateToWaiver={navigateToWaiver}
                onNavigateToDashboard={navigateToDashboard}
                onNavigateToLearnMore={navigateToLearnMore}
                onNavigateToAnnouncementDetail={navigateToAnnouncementDetail}
                onNavigateToGroperArticle={navigateToGroperArticle}
                onNavigateToColouring={navigateToColouring}
              />
            )}
          </div>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
